import {
	isArray,
	isNonEmptyArray,
	isNonEmptyObject,
	isNonEmptyString,
} from "@cayuse/shared-react.utils.type-checks";

import {
	FIELD_TYPES,
	COLUMN_HEADER_KEYS,
	DEFAULT_PROPOSAL_COLUMN_HEADERS,
	DEFAULT_AWARD_COLUMN_HEADERS,
	DEFAULT_SUBAWARD_COLUMN_HEADERS,
	DEFAULT_BUDGET_COLUMN_HEADERS,
	DEFAULT_MODIFICATION_COLUMN_HEADERS,
	CAYUSE_SUB_TYPES,
	DEFAULT_SUBAWARD_MODIFICATION_COLUMN_HEADERS,
} from "globals/constants";
import { alphaSortCollectionByKey, slugifyString, sortByAlpha } from "utils";
import { cloneDeep } from "lodash";


export const defaultAttachmentTypes = [
	{
		value: "Abstract",
		active: true
	},
	{
		value: "Application Package",
		active: true
	},
	{
		value: "Biosketch/CVBudget",
		active: true
	},
	{
		value: "Budget (internal)",
		active: true
	},
	{
		value: "Budget Justification",
		active: true
	},
	{
		value: "Cost Share Commitment Letter",
		active: true
	},
	{
		value: "F&A Waiver",
		active: true
	},
	{
		value: "IRB Documents",
		active: true
	},
	{
		value: "Letter of Intent",
		active: true
	},
	{
		value: "Other",
		active: true
	},
	{
		value: "Other Support",
		active: true
	},
	{
		value: "Preproposal",
		active: true
	},
	{
		value: "Proposal Narrative",
		active: true
	},
	{
		value: "Proposal Announcement Guidelines",
		active: true
	},
	{
		value: "SubcontractRepresentation/Certification",
		active: true
	},
	{
		value: "Subcontractor Budget",
		active: true
	},
	{
		value: "Subcontractor Budget Justification",
		active: true
	},
	{
		value: "Subcontractor Letter of Intent",
		active: true
	},
	{
		value: "Subcontractor Scope of Work",
		active: true
	},
];

export const defaultModificationTypes = [
	{
		active: true,
		value: "No-Cost Time Extension"
	},
	{
		active: true,
		value: "New Funding Increment"
	}
];

export const defaultResolutions = {
	PROPOSAL: [
		{
			active: true,
			value: "Not Funded",
		},
		{
			active: true,
			value: "Withdrawn",
		},
		{
			active: true,
			value: "Funded (Project Complete)",
		},
	],
	AWARD: [
		{
			active: true,
			value: "Admin Closed",
		},
		{
			active: true,
			value: "Unable to Negotiate",
		},
		{
			active: true,
			value: "Complete",
		},
	],
	SUBAWARD: [
		{
			active: true,
			value: "Admin Closed",
		},
		{
			active: true,
			value: "Unable to Negotiate",
		},
		{
			active: true,
			value: "Complete",
		},
	],
};

export const defaultSubawardCostCap = 0;

export const defaultCategories = {
	PROPOSAL: [
		{
			value : "Research Team",
			active : true,
			hash : "research-team",
			code : "555"
		},
		{
			value : "All F&A",
			active : true,
			hash : "all-fa",
			code : "700"
		},
		{
			value : "Equipment",
			active : true,
			hash : "equipment",
			code : "12x"
		},
		{
			value : "Travel Costs",
			active : true,
			hash : "travel-costs",
			code : "13x"
		},
		{
			value : "Participant Support Costs",
			active : true,
			hash : "participant-support-costs",
			code : "14x"
		},
		{
			value : "Other Direct Costs",
			active : true,
			hash : "other-direct-costs",
			code : "15x"
		},
		{
			value : "Subawards",
			active : true,
			hash : "subawards",
			code : "800"
		}
	]
};

export const formatColumnHeaders = (unformattedColumnHeaders, formType) => {
	const hasColumnHeaders = formType in unformattedColumnHeaders;

	if (!hasColumnHeaders) {
		console.error("Unknown formType given to actions/shell::formatColumnHeaders");

		return [];
	}

	let defaultColumnHeaders = [];
	switch (formType) {
		case COLUMN_HEADER_KEYS.PROPOSAL: defaultColumnHeaders = DEFAULT_PROPOSAL_COLUMN_HEADERS; break;
		case COLUMN_HEADER_KEYS.AWARD: defaultColumnHeaders = DEFAULT_AWARD_COLUMN_HEADERS; break;
		case COLUMN_HEADER_KEYS.SUBAWARD: defaultColumnHeaders = DEFAULT_SUBAWARD_COLUMN_HEADERS; break;
		case COLUMN_HEADER_KEYS.MODIFICATIONS: defaultColumnHeaders = DEFAULT_MODIFICATION_COLUMN_HEADERS; break;
		case COLUMN_HEADER_KEYS.SUBAWARD_MODIFICATIONS: defaultColumnHeaders = DEFAULT_SUBAWARD_MODIFICATION_COLUMN_HEADERS; break;
		case COLUMN_HEADER_KEYS.BUDGET: defaultColumnHeaders = DEFAULT_BUDGET_COLUMN_HEADERS; break;
		default: break;
	}

	const preparedColumnHeaders = prepareColumnHeaders(unformattedColumnHeaders[formType], defaultColumnHeaders);
	alphaSortCollectionByKey(preparedColumnHeaders, "columnHeader");

	return preparedColumnHeaders;
};

export const hydrateColumnHeaders = (dryColumnHeaders, columnHeaders = []) => {
	const hydratedColumnHeaders = [];

	dryColumnHeaders.forEach(dryColumnHeader => {
		const lcColumnHeader = isNonEmptyString(dryColumnHeader.columnHeader)
			? dryColumnHeader.columnHeader.toLowerCase()
			: undefined;

		const matches = lcColumnHeader
			? columnHeaders.filter(({ columnHeader }) => {
				return isNonEmptyString(columnHeader) && columnHeader.toLowerCase() === lcColumnHeader;
			})
			: [];

		if (isNonEmptyArray(matches)) {
			const cumulativeKeys = matches.reduce((keys, currentMatch) => {
				return [ ...keys, ...currentMatch.keys ];
			}, []);

			hydratedColumnHeaders.push({
				...dryColumnHeader,
				keys: cumulativeKeys
			});
		} else {
			hydratedColumnHeaders.push(dryColumnHeader);
		}
	});

	return hydratedColumnHeaders;
};

export const mergeDefaultColumnHeaders = (columnHeaders, defaultColumnHeaders = []) => {
	const mergedColumnHeaders = cloneDeep(columnHeaders);

	defaultColumnHeaders.forEach(defaultColumnHeader => {
		if (isNonEmptyString(defaultColumnHeader.columnHeader)) {
			const lcColumnHeader = defaultColumnHeader.columnHeader.toLowerCase();
			const matchIndex = columnHeaders.findIndex(({ columnHeader }) => columnHeader?.toLowerCase() === lcColumnHeader);

			if (matchIndex > -1) {
				mergedColumnHeaders[matchIndex] = {
					...defaultColumnHeader,
					...mergedColumnHeaders[matchIndex],
					keys: [
						...defaultColumnHeader.keys,
						...mergedColumnHeaders[matchIndex]?.keys
					],
					extraSettings: {
						...(defaultColumnHeader?.extraSettings ?? {}),
            ...(mergedColumnHeaders[matchIndex]?.extraSettings ?? {}),
					},
				};
			} else {
				mergedColumnHeaders.push(defaultColumnHeader);
			}
		}
	});

	return mergedColumnHeaders;
};

export const prepareColumnHeaders = (columnHeaders , defaultColumnHeaders) => {
	const mergedColumnHeaders = mergeDefaultColumnHeaders(columnHeaders, Object.values(defaultColumnHeaders));
	const uniqueColumnHeaders = deduplicateColumnHeaders(mergedColumnHeaders);

	return processColumnHeaderExtraFeatures(uniqueColumnHeaders);
};

export const deduplicateColumnHeaders = columnHeaders => {
	const colMap = new Map();

	if (isNonEmptyArray(columnHeaders)) {
		columnHeaders.forEach(columnHeader => {
			const shortLabel = columnHeader.columnHeader || "";

			if (colMap.has(shortLabel)) {
				const match = colMap.get(shortLabel);
				match.keys.push(...columnHeader.keys);
				colMap.set(shortLabel, match);
			} else {
				colMap.set(shortLabel, columnHeader);
			}
		});

		return Array.from(colMap.values());
	} else {
		return [];
	}
};

export const processColumnHeaderExtraFeatures = columnHeaders => {
	return columnHeaders.map(columnHeader => {
		if (columnHeader.extraSettings?.enableTime && columnHeader.type !== FIELD_TYPES.DATETIME) {
			return {
				...columnHeader,
				type: FIELD_TYPES.DATETIME
			};
		} else if (columnHeader.cayuseSubType === CAYUSE_SUB_TYPES.PROGRAM_ROUND_INTERNAL_CLOSING_DATE ||
			       columnHeader.cayuseSubType === CAYUSE_SUB_TYPES.PROGRAM_ROUND_EXTERNAL_CLOSING_DATE) {
			return {
				...columnHeader,
				type: FIELD_TYPES.DATE
			};
		}

		return columnHeader;
	});
};

export const getColumnHeaderByShortLabel = (columnHeaders, shortLabel) => {
	const lcShortLabel = shortLabel.toLowerCase();

	return columnHeaders.find(({ columnHeader }) => columnHeader.toLowerCase() === lcShortLabel);
};

const useSlugify = item => {
	return slugifyString(item.value);
};

const useValue = item => {
	return item.value;
};

export const setArrayItemHash = (data, transform) => {
	if (isArray(data)) {
		return data
			.map(item => {
				item.hash = transform(item);
				return item;
			})
			.sort(sortByAlpha);
	} else {
		if (isNonEmptyObject(data)) {
			for (let key in data) {
				setArrayItemHash(data[key], transform);
			}

			return data;
		}
	}
};

export const slugifyNonEmptyData = (data, defaultData) => {
	return isNonEmptyArray(data) ? setArrayItemHash(data, useSlugify) : setArrayItemHash(defaultData, useSlugify);
};

export const valueifyNonEmptyData = (data, defaultData) => {
	return isNonEmptyArray(data) ? setArrayItemHash(data, useValue) : setArrayItemHash(defaultData, useValue);
};

export const mapFormColumnHeaders = (formColumnHeaders, getState) => {
	const nextFormColumnHeaders = cloneDeep(getState().tenant.formColumnHeaders);

	if (isNonEmptyObject(formColumnHeaders)) {
		for (let formType in formColumnHeaders) {
			nextFormColumnHeaders[formType] = formatColumnHeaders(formColumnHeaders, formType);
		}
	}

	return nextFormColumnHeaders;
};
